import axios, { AxiosInstance, AxiosRequestConfig } from "axios";

import { LGT_SERVER_URL } from "@/lib/env";

export class RequestService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: LGT_SERVER_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem("token");

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const token = await this.refreshToken();
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return this.api(originalRequest);
        }

        return Promise.reject(error);
      },
    );
  }

  async get<T>(
    path: string,
    params?: object,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.api.get(path, { params, ...config });
    return response.data;
  }

  async post<T>(path: string, body: object): Promise<T> {
    const response = await this.api.post(path, body);
    return response.data;
  }

  async put<T>(path: string, body: object): Promise<T> {
    const response = await this.api.put(path, body);
    return response.data;
  }

  async patch<T>(path: string, body: object): Promise<T> {
    const response = await this.api.patch(path, body);
    return response.data;
  }

  async delete<T>(path: string): Promise<T> {
    const response = await this.api.delete(path);
    return response.data;
  }

  public async refreshToken(organizationId?: string) {
    const response = await fetch(`${LGT_SERVER_URL}/auth/refresh`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organization_id: organizationId }),
    });

    if (!response.ok) {
      await this.endSession();
      localStorage.removeItem("token");
      window.location.href = "/";
    }

    const data = await response.json();

    if (data.token) {
      localStorage.setItem("token", data.token);
      return data.token;
    } else {
      throw new Error("No token found in response");
    }
  }

  private async endSession(sessionId?: string) {
    if (sessionId) {
      const response = await fetch(
        `https://api.workos.com/user_management/sessions/logout?session_id=${sessionId}`,
      );

      if (!response.ok) {
        throw new Error("Failed to end WorkOS session");
      }
    }
  }
}
